import { useEffect, useMemo, useState } from 'react';
import type { FacilityFlowCoreModulesInvoicesDtosBillableObjectDto } from '../../../api/generated/models';

/**
 * Shared bits of the two invoice runs on `InvoiceCreatePage` (monthly run + standing
 * invoices, FACF-106): period options, control classes and the row-selection state.
 */

export type BillableRow = FacilityFlowCoreModulesInvoicesDtosBillableObjectDto;

export const CURRENT_YEAR = new Date().getFullYear();
export const CURRENT_MONTH = new Date().getMonth() + 1;
export const YEAR_OPTIONS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1];
export const MONTH_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export const PERIOD_SELECT_CLASS =
  'w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';

export const ROW_CHECKBOX_CLASS =
  'h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30 disabled:opacity-50';

/** Positions of the active offer still lack a billing kind („zu prüfen"). */
export const isBlockedByReview = (row: BillableRow) => (row.needsReviewCount ?? 0) > 0;

/**
 * Rows the backend would reject: positions to review, or (standing run) months of the period already
 * billed by monthly invoices — invoices.standingOverlapsBilledMonths — or a period starting before
 * the offer month — invoices.standingStartsBeforeOffer (FAFD-27). Monthly rows never carry the
 * standing flags.
 */
export const isBlocked = (row: BillableRow) =>
  isBlockedByReview(row) || row.overlapsBilledMonths === true || row.startsBeforeOffer === true;

const totalNetOf = (row: BillableRow) => row.totalNet ?? 0;

/**
 * Multi-select over the not-yet-invoiced rows of a proposal list. The selection is
 * cleared whenever `resetKey` (the chosen period) changes. `amountOf` picks the value
 * summed into `selectedTotal` (default: `totalNet`).
 */
export function useBillableSelection(
  rows: BillableRow[],
  resetKey: string | number,
  amountOf: (row: BillableRow) => number = totalNetOf,
) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelected({});
  }, [resetKey]);

  const selectable = useMemo(
    // why: rows the backend would reject (positions to review, months already billed) cannot be selected.
    () => rows.filter((r) => !r.alreadyInvoiced && !isBlocked(r)),
    [rows],
  );

  // why: only ids that are still selectable count — a row that became invoiced (refetch)
  // must not be submitted again just because it was ticked before.
  const selectedRows = useMemo(
    () => selectable.filter((r) => r.objectId && selected[r.objectId]),
    [selectable, selected],
  );

  const selectedIds = useMemo(
    () => selectedRows.map((r) => r.objectId as string),
    [selectedRows],
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((acc, r) => acc + amountOf(r), 0),
    [selectedRows, amountOf],
  );

  const allSelectableSelected =
    selectable.length > 0
    && selectable.every((r) => r.objectId && selected[r.objectId]);

  const toggleAll = () => {
    if (allSelectableSelected) {
      setSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of selectable) {
      if (r.objectId) next[r.objectId] = true;
    }
    setSelected(next);
  };

  const toggleOne = (objectId?: string) => {
    if (!objectId) return;
    setSelected((s) => ({ ...s, [objectId]: !s[objectId] }));
  };

  const isSelected = (objectId?: string) => !!(objectId && selected[objectId]);

  const clear = () => setSelected({});

  return {
    selectable,
    selectedIds,
    selectedTotal,
    allSelectableSelected,
    toggleAll,
    toggleOne,
    isSelected,
    clear,
  };
}
